import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { CurrentContextService } from '../../core/context/current-context.service';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { InventoryPageHintComponent } from './inventory-page-hint.component';
import { CatalogService, InventoryKpiDashboard } from './catalog.service';
import { bindInventoryLiveUpdates } from './inventory-live-updates';

@Component({
  standalone: true,
  selector: 'ain-inventory-kpi-dashboard',
  imports: [AinPageComponent, AinStateComponent, InventoryPageHintComponent, RouterLink, ButtonModule],
  template: `
    <ain-page [title]="t('catalog.kpi.title')" [description]="t('catalog.kpi.description')">
      <ain-inventory-page-hint pageKey="kpi" />
      @if (loading()) { <ain-state type="loading" [title]="t('catalog.loading')" /> }
      @else if (!kpi()) { <ain-state type="error" [title]="t('catalog.unavailable')" /> }
      @else {
        <div class="toolbar">
          <button pButton type="button" severity="secondary" [label]="t('catalog.action.refresh')" (click)="load()"></button>
        </div>
        <section class="stats">
          <article><strong>{{ t('catalog.kpi.onHand') }}</strong><span>{{ kpi()!.totalOnHandQuantity }}</span></article>
          <article><strong>{{ t('catalog.kpi.reservations') }}</strong><span>{{ kpi()!.activeReservations }}</span></article>
          <article><strong>{{ t('catalog.kpi.putAway') }}</strong><span>{{ kpi()!.pendingPutAwayTasks }}</span></article>
          <article><strong>{{ t('catalog.kpi.pick') }}</strong><span>{{ kpi()!.pendingPickTasks }}</span></article>
          <article><strong>{{ t('catalog.kpi.packShip') }}</strong><span>{{ kpi()!.pendingPackShipTasks }}</span></article>
          <article><strong>{{ t('catalog.kpi.drafts') }}</strong><span>{{ kpi()!.draftMovements }}</span></article>
          <article><strong>{{ t('catalog.kpi.quarantineBins') }}</strong><span>{{ kpi()!.quarantineBins }}</span></article>
          <article><strong>{{ t('catalog.kpi.inbound') }}</strong><span>{{ kpi()!.scheduledInboundShipments }}</span></article>
          <article><strong>{{ t('catalog.kpi.waves') }}</strong><span>{{ kpi()!.openPickWaves }}</span></article>
          <article class="warn"><strong>{{ t('catalog.kpi.quarantinedLots') }}</strong><span>{{ kpi()!.quarantinedLots }}</span></article>
        </section>
        <nav class="links">
          <a routerLink="/inventory/operator">{{ t('nav.inventory.operator') }}</a>
          <a routerLink="/inventory/warehouse-tasks">{{ t('nav.inventory.warehouseTasks') }}</a>
          <a routerLink="/inventory/movements">{{ t('nav.inventory.movements') }}</a>
        </nav>
      }
    </ain-page>
  `,
  styles: [`
    .toolbar { margin-bottom: 12px; }
    .stats { display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: 10px; }
    article { border: 1px solid #e2e8f0; border-radius: 10px; padding: 10px; display: grid; gap: 4px; }
    article.warn { border-color: #fecaca; background: #fff1f2; }
    .links { margin-top: 16px; display: flex; gap: 12px; flex-wrap: wrap; }
    .links a { color: #1d4ed8; font-weight: 600; }
  `]
})
export class InventoryKpiDashboardComponent implements OnInit {
  private readonly service = inject(CatalogService);
  private readonly localization = inject(LocalizationService);
  private readonly liveUpdateService = inject(LiveUpdateService);
  private readonly contextService = inject(CurrentContextService);
  private readonly destroyRef = inject(DestroyRef);
  readonly loading = signal(true);
  readonly kpi = signal<InventoryKpiDashboard | null>(null);
  t = (k: string) => this.localization.translate(k);

  ngOnInit(): void {
    bindInventoryLiveUpdates(
      { liveUpdateService: this.liveUpdateService, contextService: this.contextService, destroyRef: this.destroyRef },
      () => this.load()
    );
    this.load();
  }

  load(): void {
    this.service.getInventoryKpiDashboard().subscribe({
      next: row => { this.kpi.set(row); this.loading.set(false); },
      error: () => { this.kpi.set(null); this.loading.set(false); }
    });
  }
}
